import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AccordionProps,
  Box,
  Grid,
  GridItem,
  Text,
} from '@everlywell/ui-kit';
import { Link } from 'gatsby-link';
import { GatsbyImage as Img } from 'gatsby-plugin-image';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import renderRichText, {
  ContentfulRichText,
} from 'utils/contentful/renderRichText';
import { isRetiredSupportHref } from 'utils/supportLinks';
import { FAQCase } from 'utils/types';

export type FaqItem = {
  question: string;
  answer: ContentfulRichText;
  cases?: FAQCase[];
};

type SlotOverride = {
  sx?: any;
};

export type FaqAccordionSlotOverrides = {
  root?: SlotOverride;
  item?: SlotOverride;
  button?: SlotOverride;
  buttonText?: SlotOverride;
  icon?: SlotOverride;
  panel?: SlotOverride;
  panelImage?: SlotOverride;
  panelTitle?: SlotOverride;
  panelDescription?: SlotOverride;
};

export type FaqAccordionProps = {
  faqs: FaqItem[];
  width?: AccordionProps['width'];
  isExpanded: boolean;
  // The tracking label for when the accordion is clicked. Default is 'FaqV2'.
  trackingLabel?: string;
  // When true, the accordion button will have the default size and not wrapper in a Text component. Default is false.
  useAccordionButtonTextElement?: boolean;
  slots?: FaqAccordionSlotOverrides;
};

const options: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Text>{children}</Text>,
    [INLINES.HYPERLINK]: (node, children) =>
      isRetiredSupportHref(node.data.uri) ? (
        <>{children}</>
      ) : (
        <Link to={node.data.uri} style={{ textDecoration: 'underline' }}>
          {children}
        </Link>
      ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <ul style={{ listStyleType: 'disc', marginInlineStart: '1em' }}>
        {children}
      </ul>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <ol style={{ listStyleType: 'decimal', marginInlineStart: '1em' }}>
        {children}
      </ol>
    ),
  },
};

/**
 * Renders the FaqAccordion component. A list of accordions with rich text content.
 * @param props [FaqAccordionProps]
 * @param props.faqs [FaqItem[]] List of FAQ items
 * @param props.faqs[].question [string] The question
 * @param props.faqs[].answer [ContentfulRichText] The answer
 * @param props.faqs[].cases [FAQCase[]] List of FAQ cases
 *
 * @returns JSX.Element
 */
export default function FaqAccordion({
  faqs,
  width = '100%',
  isExpanded = false,
  trackingLabel = 'FaqV2',
  useAccordionButtonTextElement = true,
  slots,
}: FaqAccordionProps) {
  const handleQuestionOpen = (question: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.FAQ_CLICK,
      data: { question: question, contentfulSection: trackingLabel },
    });
  };

  const resultFaqs = isExpanded ? faqs : faqs.slice(0, 5);

  return (
    <Accordion allowMultiple width={width} maxWidth="100%" sx={slots?.root?.sx}>
      {resultFaqs.map((faq, index) => (
        <AccordionItem key={index} sx={slots?.item?.sx}>
          <AccordionButton
            onClick={() => handleQuestionOpen(faq.question)}
            sx={{
              fontWeight: 'medium',
              // The theme replaces Chakra's accordion button base style, which
              // is where the focus ring lived, so keyboard focus lands here
              // with a transparent outline unless we redraw it.
              _focusVisible: {
                outlineColor: 'moonlight.light',
                outlineWidth: '2px',
                outlineStyle: 'solid',
                outlineOffset: '2px',
              },
              ...(slots?.button?.sx ?? {}),
            }}
          >
            {useAccordionButtonTextElement ? (
              <Text size="xl" fontWeight="medium" sx={slots?.buttonText?.sx}>
                {faq.question}
              </Text>
            ) : (
              faq.question
            )}
            <AccordionIcon sx={slots?.icon?.sx} />
          </AccordionButton>
          <AccordionPanel
            fontWeight="normal"
            paddingLeft="1"
            sx={{
              '&>*:not(:last-child)': {
                marginBottom: 4,
              },
              ...(slots?.panel?.sx ?? {}),
            }}
          >
            {renderRichText(faq.answer, options)}
            {faq?.cases?.length ? (
              <Grid columns={[1, 2, 3]} marginTop={8}>
                {faq?.cases?.length &&
                  faq.cases.map((item, i): any => (
                    <GridItem key={i}>
                      <Box sx={slots?.panelImage?.sx}>
                        <Img
                          image={item.image.gatsbyImageData}
                          alt={item.image.description || item.title}
                        />
                      </Box>
                      <Text
                        size="lg"
                        color="tints.black"
                        marginTop={4}
                        marginBottom={2}
                        sx={slots?.panelTitle?.sx}
                      >
                        {item.title}
                      </Text>

                      <Box sx={slots?.panelDescription?.sx}>
                        {renderRichText(item.description, options)}
                      </Box>
                    </GridItem>
                  ))}
              </Grid>
            ) : null}
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
